// services/TransactionService.js - Service for handling user transactions and rewards
import { collection, addDoc, getDocs, query, orderBy, where, doc, updateDoc, getDoc, increment } from 'firebase/firestore';
import { db } from '../firebase/config';

class TransactionService {
  constructor() {
    this.transactionsCollection = 'user_transactions';
    this.usersCollection = 'users';
  }

  /**
   * Add a reward transaction for a user
   */
  async addRewardTransaction(userId, amount, type, description, relatedId = null) {
    try {
      const transaction = {
        userId,
        amount,
        type, // 'report_reward', 'bonus', 'withdrawal', etc.
        description,
        relatedId, // report ID, promotion ID, etc.
        status: 'completed',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Add transaction record
      const docRef = await addDoc(collection(db, this.transactionsCollection), transaction);
      
      // Update user balance
      await this.updateUserBalance(userId, amount);

      return {
        success: true,
        transactionId: docRef.id,
        message: 'Reward added successfully'
      };
    } catch (error) {
      console.error('Error adding reward transaction:', error);
      return {
        success: false,
        error: error.message || 'Failed to add reward transaction'
      };
    }
  }

  /**
   * Update user balance
   */
  async updateUserBalance(userId, amount) {
    try {
      const userRef = doc(db, this.usersCollection, userId);
      
      // Check if user document exists
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        // Create user document with initial balance
        await updateDoc(userRef, {
          balance: amount,
          totalEarned: amount,
          updatedAt: new Date()
        });
      } else {
        // Update existing balance
        await updateDoc(userRef, {
          balance: increment(amount),
          totalEarned: increment(amount),
          updatedAt: new Date()
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating user balance:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user's transaction history
   */
  async getUserTransactions(userId) {
    try {
      const q = query(
        collection(db, this.transactionsCollection),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const transactions = [];
      
      querySnapshot.forEach((doc) => {
        transactions.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return {
        success: true,
        data: transactions
      };
    } catch (error) {
      console.error('Error fetching user transactions:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch transactions',
        data: []
      };
    }
  }

  /**
   * Get user balance
   */
  async getUserBalance(userId) {
    try {
      const userRef = doc(db, this.usersCollection, userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return {
          success: true,
          balance: userData.balance || 0,
          totalEarned: userData.totalEarned || 0
        };
      } else {
        return {
          success: true,
          balance: 0,
          totalEarned: 0
        };
      }
    } catch (error) {
      console.error('Error fetching user balance:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch balance',
        balance: 0,
        totalEarned: 0
      };
    }
  }

  /**
   * Get all transactions (for admin)
   */
  async getAllTransactions() {
    try {
      const q = query(
        collection(db, this.transactionsCollection),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const transactions = [];
      
      querySnapshot.forEach((doc) => {
        transactions.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return {
        success: true,
        data: transactions
      };
    } catch (error) {
      console.error('Error fetching all transactions:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch transactions',
        data: []
      };
    }
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount) {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  /**
   * Get transaction type display info
   */
  getTransactionTypeInfo(type) {
    const typeConfig = {
      report_reward: {
        label: 'Report Reward',
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        iconName: 'Award'
      },
      bonus: {
        label: 'Bonus',
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        iconName: 'DollarSign'
      },
      withdrawal: {
        label: 'Withdrawal',
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        iconName: 'ArrowDownLeft'
      }
    };

    return typeConfig[type] || {
      label: type,
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      iconName: 'Coins'
    };
  }
}

// Create and export a singleton instance
const transactionService = new TransactionService();
export default transactionService;